/**
 * Sign-in screen, for the whole site (17 Oct 2026 redesign): a dark field of code and dubizzle logos that light up
 * where the pointer goes (CodeField), the 3D flame and the dubizzle group wordmark, and the sign-in card in dark
 * glass. The card's markup and behaviour are unchanged; the server session replaces the prototype's vault.
 */
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Me } from '../lib/api';
import { loginFailed, loginPending } from '../lib/analytics';
import { lastSignIn, reducedMotion, sleep, useMe } from '../lib/session';
import { APP_VERSION, BUILT_AT } from '../lib/build';
import { CodeField } from '../components/CodeField';
import { TfMark, Wordmark } from '../taskforce/brand';
import flame3d from '../assets/brand/flame-3d.webp';
import '../styles/signin.css';

function greeting(): string {
  const h = new Date().getHours();
  return h < 5 ? 'Burning the midnight oil?' : h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

const LOCK_ICON = (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <rect x="4" y="9" width="12" height="8" rx="2" />
    <path d="M7 9V6.5a3 3 0 0 1 6 0V9" />
  </svg>
);

const GOOGLE_PROBLEMS: Record<string, string> = {
  'no-account': 'You don’t have an account yet. Contact your administrator for access.',
  domain: 'Use your work Google account, from one of the organization’s companies.',
  inactive: 'Your account is suspended. Contact your administrator.',
  expired: 'That sign-in was interrupted or took too long. Try again.',
  cancelled: 'Google sign-in was cancelled.',
  off: 'Google sign-in is not available right now. Use your password.',
  unverified: 'Google has not verified that email address. Use your password instead.',
  failed: 'Google sign-in did not work. Try again, or use your password.',
};

/** Shared shell: the code field, the hero and the glass card. */
function LoginShell({ leaving, children }: { leaving: boolean; children: React.ReactNode }) {
  return (
    <main id="login" className={'login' + (leaving ? ' leaving' : '')}>
      <div className="lbg" aria-hidden="true">
        <CodeField />
      </div>
      <div className="lscene">
        <div className="lhero">
          <img className="lflame" src={flame3d} alt="" width={256} height={256} />
          <div className="lhero-t">
            <p className="lkicker">Task Force</p>
            <p className="lhead">
              Where work
              <br />
              <span>goes live.</span>
            </p>
            <p className="lsub">Task Force · Attendance · Reports</p>
          </div>
        </div>
        <Wordmark height={44} />
      </div>
      {children}
    </main>
  );
}

function Brand() {
  return (
    <div className="lbrand">
      <TfMark size={26} />
      <span>Task Force</span>
    </div>
  );
}

function Footer() {
  return (
    <>
      <p className="loginver">
        v{APP_VERSION}, built {BUILT_AT}
      </p>
    </>
  );
}

/** Card state shared by both steps: error box, shake, spinner, success tick. */
function useCardState() {
  const [err, setErr] = useState('');
  const [shake, setShake] = useState(0);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const showErr = (m: string) => {
    setErr(m);
    setShake((n) => n + 1);
  };
  return { err, setErr, shake, busy, setBusy, ok, setOk, leaving, setLeaving, showErr };
}

export function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const me = useMe();
  const [step, setStep] = useState<'signin' | 'change'>('signin');
  const [name, setName] = useState(() => lastSignIn.get() ?? '');
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const [fails, setFails] = useState(0);
  const s = useCardState();
  // How people sign in: Google (12 Oct 2026: the only way), and passwords only while the emergency switch is on.
  const [opts, setOpts] = useState<{ google: boolean; password: boolean } | null>(null);
  useEffect(() => {
    api<{ google: boolean; password: boolean }>('/api/auth/options')
      .then(setOpts)
      .catch(() => setOpts({ google: true, password: false }));
  }, []);
  const google = !!opts?.google;
  // Back from Google with a problem: say what happened (no personal data in the address).
  useEffect(() => {
    const code = new URLSearchParams(location.search).get('google');
    if (!code) return;
    loginFailed('google', code);
    s.showErr(GOOGLE_PROBLEMS[code] ?? GOOGLE_PROBLEMS.failed!);
    window.history.replaceState(null, '', '/sign-in');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const nameRef = useRef<HTMLInputElement>(null);
  const pwRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLFormElement>(null);

  // Where to go afterwards: the page that sent us here (deep links such as ?member= survive sign-in).
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Opened with a session already (for example remember me): skip the form. Only checked once, on arrival;
  // a sign-in made on this page finishes its own animation before moving on.
  const checked = useRef(false);
  useEffect(() => {
    if (me.isLoading || checked.current) return;
    checked.current = true;
    if (me.data?.mustChangePassword) setStep('change');
    else if (me.data) navigate(from, { replace: true });
  }, [me.isLoading, me.data, navigate, from]);

  useEffect(() => {
    setTimeout(() => (name ? pwRef.current : nameRef.current)?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restart the shake animation each time an error is shown.
  useEffect(() => {
    const card = cardRef.current;
    if (!card || !s.shake) return;
    card.classList.remove('shake');
    void card.offsetWidth;
    card.classList.add('shake');
  }, [s.shake]);

  async function finish(user: Me) {
    s.setOk(true);
    qc.setQueryData(['me'], user);
    const reduce = reducedMotion();
    await sleep(reduce ? 0 : 450);
    s.setLeaving(true);
    await sleep(reduce ? 0 : 320);
    if (mounted.current) navigate(from, { replace: true, state: { entering: true } });
  }

  async function signIn() {
    if (s.busy) return;
    s.setErr('');
    const n = name.trim();
    if (!n && !pw) {
      s.showErr('Enter your name or work email, and your password.');
      nameRef.current?.focus();
      return;
    }
    if (!n) {
      s.showErr('Enter your name or work email.');
      nameRef.current?.focus();
      return;
    }
    if (!pw) {
      s.showErr('Enter your password.');
      pwRef.current?.focus();
      return;
    }
    s.setBusy(true);
    await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 40))); // let the spinner appear first
    try {
      const res = await api<{ user: Me; mustChangePassword: boolean }>('/api/auth/login', {
        method: 'POST',
        json: { identifier: n, password: pw, rememberMe: true },
      });
      lastSignIn.set(n);
      // Analytics: counted as a sign-in once the person is known (lib/analytics.ts).
      loginPending.set('password');
      if (res.mustChangePassword) {
        qc.setQueryData(['me'], res.user);
        s.setBusy(false);
        setStep('change');
        return;
      }
      setPw('');
      s.setBusy(false);
      await finish(res.user);
    } catch (e) {
      s.setBusy(false);
      setPw('');
      loginFailed('password', e instanceof ApiError ? e.code : 'NETWORK');
      if (e instanceof ApiError && (e.code === 'LOCKED' || e.code === 'RATE_LIMITED')) s.showErr(e.message);
      else if (e instanceof ApiError && e.code === 'BAD_CREDENTIALS') {
        const f = fails + 1;
        setFails(f);
        s.showErr(
          'The name, email or password is incorrect. Check them and try again.' +
            (f >= 3 ? ' Passwords are case-sensitive; ask HR if you have forgotten yours.' : ''),
        );
      } else
        s.showErr(
          e instanceof ApiError ? e.message : 'Could not reach the server. Check your connection and try again.',
        );
      pwRef.current?.focus();
    }
  }

  const onKey = (which: 'name' | 'pw') => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (which === 'name' && !pw) {
      pwRef.current?.focus();
      return;
    }
    void signIn();
  };

  if (step === 'change') return <ChangePassword onDone={finish} card={s} />;
  if (!opts?.password) return <GoogleOnly from={from} ready={!!opts} google={!opts || opts.google} card={s} />;

  return (
    <LoginShell leaving={s.leaving}>
      <form
        id="loginForm"
        ref={cardRef}
        className={'logincard' + (s.err ? ' invalid' : '') + (s.shake ? ' shake' : '') + (s.leaving ? ' leaving' : '')}
        autoComplete="on"
        noValidate
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void signIn();
        }}
      >
        <Brand />
        <h1 id="loginHello">{greeting()}</h1>
        <label htmlFor="loginName">Name or work email</label>
        <div className="fld">
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <circle cx="10" cy="7" r="3.5" />
            <path d="M3.5 17c1.2-3.2 3.7-4.8 6.5-4.8s5.3 1.6 6.5 4.8" />
          </svg>
          <input
            id="loginName"
            ref={nameRef}
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            readOnly={s.busy}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              s.setErr('');
            }}
            onKeyDown={onKey('name')}
          />
        </div>
        <label htmlFor="loginPw">Password</label>
        <div className="fld pwrow">
          {LOCK_ICON}
          <input
            id="loginPw"
            ref={pwRef}
            name="password"
            type={show ? 'text' : 'password'}
            autoComplete="current-password"
            required
            readOnly={s.busy}
            value={pw}
            onChange={(e) => {
              setPw(e.target.value);
              s.setErr('');
            }}
            onKeyDown={onKey('pw')}
          />
          <button
            type="button"
            id="pwToggle"
            className="btn"
            aria-pressed={show}
            aria-label={show ? 'Hide password' : 'Show password'}
            onClick={() => setShow((v) => !v)}
          >
            {show ? 'Hide' : 'Show'}
          </button>
        </div>
        <button
          type="button"
          id="loginBtn"
          className={'btn primary' + (s.busy ? ' busy' : '') + (s.ok ? ' ok' : '')}
          aria-busy={s.busy}
          onClick={() => void signIn()}
        >
          <span className="spin" aria-hidden="true"></span>
          <span className="lbl">{s.busy ? 'Signing in…' : s.ok ? 'Signed in' : 'Sign in'}</span>
        </button>
        {google && (
          <>
            <div className="lor" aria-hidden="true">
              <span>or</span>
            </div>
            <GoogleButton from={from} />
          </>
        )}
        <div id="loginErr" className={'loginerr' + (s.err ? ' show' : '')} role="alert" aria-live="assertive">
          {s.err}
        </div>
        <Footer />
      </form>
    </LoginShell>
  );
}

/** The Google mark and "Continue with Google": the sign-in starts on the server, never with a password here. */
function GoogleButton({ from }: { from: string }) {
  return (
    <a
      id="googleBtn"
      className="gbtn"
      href={`/api/auth/google/start?from=${encodeURIComponent(from)}`}
      onClick={() => loginPending.set('google')}
    >
      <svg viewBox="0 0 18 18" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
        />
        <path
          fill="#34A853"
          d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
        />
        <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
        <path
          fill="#EA4335"
          d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
        />
      </svg>
      Continue with Google
    </a>
  );
}

/** The sign-in card (12 Oct 2026): Google only. */
function GoogleOnly({
  from,
  ready,
  google,
  card: s,
}: {
  from: string;
  ready: boolean;
  google: boolean;
  card: ReturnType<typeof useCardState>;
}) {
  return (
    <LoginShell leaving={s.leaving}>
      <div id="loginForm" className={'logincard' + (s.err ? ' invalid' : '')}>
        <Brand />
        <h1 id="loginHello">{greeting()}</h1>
        <p className="gsub">Sign in with your work Google account.</p>
        {google ? (
          <div className={ready ? '' : 'gwait'}>
            <GoogleButton from={from} />
          </div>
        ) : (
          <p className="gsub">Sign-in is not set up yet. Ask HR for help.</p>
        )}
        <div id="loginErr" className={'loginerr' + (s.err ? ' show' : '')} role="alert" aria-live="assertive">
          {s.err}
        </div>
        <p className="gnote">For your security you are asked to sign in again every 8 hours.</p>
        <Footer />
      </div>
    </LoginShell>
  );
}

/** "Choose a new password" step, in the same card style (brief 9.2). */
function ChangePassword({
  onDone,
  card: s,
}: {
  onDone: (u: Me) => Promise<void>;
  card: ReturnType<typeof useCardState>;
}) {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const curRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLFormElement>(null);
  useEffect(() => curRef.current?.focus(), []);
  useEffect(() => {
    const card = cardRef.current;
    if (!card || !s.shake) return;
    card.classList.remove('shake');
    void card.offsetWidth;
    card.classList.add('shake');
  }, [s.shake]);

  async function submit() {
    if (s.busy) return;
    s.setErr('');
    if (!cur || !next) return s.showErr('Enter your current password and a new one.');
    if (next !== again) return s.showErr('The new passwords do not match.');
    s.setBusy(true);
    try {
      const res = await api<{ user: Me }>('/api/auth/change-password', {
        method: 'POST',
        json: { currentPassword: cur, newPassword: next },
      });
      s.setBusy(false);
      await onDone(res.user);
    } catch (e) {
      s.setBusy(false);
      s.showErr(e instanceof ApiError ? e.message : 'Could not reach the server. Try again.');
    }
  }

  const field = (
    id: string,
    label: string,
    value: string,
    set: (v: string) => void,
    auto: string,
    ref?: React.Ref<HTMLInputElement>,
  ) => (
    <>
      <label htmlFor={id}>{label}</label>
      <div className="fld">
        {LOCK_ICON}
        <input
          id={id}
          ref={ref}
          type="password"
          autoComplete={auto}
          value={value}
          readOnly={s.busy}
          onChange={(e) => {
            set(e.target.value);
            s.setErr('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void submit();
            }
          }}
        />
      </div>
    </>
  );

  return (
    <LoginShell leaving={s.leaving}>
      <form
        ref={cardRef}
        className={'logincard' + (s.err ? ' invalid' : '') + (s.shake ? ' shake' : '') + (s.leaving ? ' leaving' : '')}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Brand />
        <h1>Choose a new password</h1>
        <p className="saved">Use at least 12 characters. You will use it from now on instead of the one HR gave you.</p>
        {field('cpCurrent', 'Current password', cur, setCur, 'current-password', curRef)}
        {field('cpNew', 'New password', next, setNext, 'new-password')}
        {field('cpAgain', 'New password again', again, setAgain, 'new-password')}
        <button
          type="button"
          id="loginBtn"
          className={'btn primary' + (s.busy ? ' busy' : '') + (s.ok ? ' ok' : '')}
          aria-busy={s.busy}
          onClick={() => void submit()}
        >
          <span className="spin" aria-hidden="true"></span>
          <span className="lbl">{s.busy ? 'Saving…' : s.ok ? 'Signed in' : 'Save and continue'}</span>
        </button>
        <div className={'loginerr' + (s.err ? ' show' : '')} role="alert" aria-live="assertive">
          {s.err}
        </div>
        <Footer />
      </form>
    </LoginShell>
  );
}

/**
 * The welcome email's "Log in now" link (/welcome#t=…): shows who the account is for and asks for a new password;
 * saving it signs them in. The token is read from the address fragment and then removed from the address bar.
 */
export function Welcome() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const s = useCardState();
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('t') ?? '');
  const [who, setWho] = useState<{ displayName: string; email: string } | null>(null);
  const [dead, setDead] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const nextRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    window.history.replaceState(null, '', '/welcome');
    if (!token) {
      setDead(
        'This link is not complete. Open it again from your welcome email, or sign in with your one-time password.',
      );
      return;
    }
    api<{ displayName: string; email: string }>('/api/auth/invite/check', { method: 'POST', json: { token } })
      .then((w) => {
        setWho(w);
        setTimeout(() => nextRef.current?.focus(), 0);
      })
      .catch((e) => setDead(e instanceof ApiError ? e.message : 'Could not reach the server. Try again.'));
  }, [token]);
  useEffect(() => {
    const card = cardRef.current;
    if (!card || !s.shake) return;
    card.classList.remove('shake');
    void card.offsetWidth;
    card.classList.add('shake');
  }, [s.shake]);

  async function submit() {
    if (s.busy || !who) return;
    s.setErr('');
    if (!next) return s.showErr('Choose a password.');
    if (next !== again) return s.showErr('The passwords do not match.');
    s.setBusy(true);
    try {
      const res = await api<{ user: Me }>('/api/auth/invite/accept', {
        method: 'POST',
        json: { token, newPassword: next, rememberMe: true },
      });
      lastSignIn.set(who.email);
      s.setBusy(false);
      s.setOk(true);
      qc.setQueryData(['me'], res.user);
      const reduce = reducedMotion();
      await sleep(reduce ? 0 : 450);
      s.setLeaving(true);
      await sleep(reduce ? 0 : 320);
      navigate('/', { replace: true, state: { entering: true } });
    } catch (e) {
      s.setBusy(false);
      if (e instanceof ApiError && e.code?.startsWith('INVITE_')) setDead(e.message);
      else s.showErr(e instanceof ApiError ? e.message : 'Could not reach the server. Try again.');
    }
  }

  const field = (
    id: string,
    label: string,
    value: string,
    set: (v: string) => void,
    ref?: React.Ref<HTMLInputElement>,
  ) => (
    <>
      <label htmlFor={id}>{label}</label>
      <div className="fld">
        {LOCK_ICON}
        <input
          id={id}
          ref={ref}
          type="password"
          autoComplete="new-password"
          value={value}
          readOnly={s.busy}
          onChange={(e) => {
            set(e.target.value);
            s.setErr('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void submit();
            }
          }}
        />
      </div>
    </>
  );

  return (
    <LoginShell leaving={s.leaving}>
      <form
        ref={cardRef}
        id="welcomeForm"
        className={'logincard' + (s.err ? ' invalid' : '') + (s.shake ? ' shake' : '') + (s.leaving ? ' leaving' : '')}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Brand />
        {dead ? (
          <>
            <h1>Let&apos;s get you in</h1>
            <p className="saved" id="welcomeDead">
              {dead}
            </p>
            <button type="button" id="loginBtn" className="btn primary" onClick={() => navigate('/sign-in')}>
              <span className="lbl">Go to sign in</span>
            </button>
          </>
        ) : !who ? (
          <p className="saved">Checking your link…</p>
        ) : (
          <>
            <h1 id="welcomeHello">Welcome, {who.displayName.split(/\s+/)[0]}</h1>
            <p className="saved">
              Choose your password for <b>{who.email}</b>. Use at least 12 characters; you will sign in with it from now
              on.
            </p>
            {/* For password managers: the account this password belongs to. */}
            <input type="email" autoComplete="username" value={who.email} readOnly hidden />
            {field('wNew', 'New password', next, setNext, nextRef)}
            {field('wAgain', 'New password again', again, setAgain)}
            <button
              type="button"
              id="loginBtn"
              className={'btn primary' + (s.busy ? ' busy' : '') + (s.ok ? ' ok' : '')}
              aria-busy={s.busy}
              onClick={() => void submit()}
            >
              <span className="spin" aria-hidden="true"></span>
              <span className="lbl">{s.busy ? 'Saving…' : s.ok ? 'Signed in' : 'Save and sign in'}</span>
            </button>
            <div className={'loginerr' + (s.err ? ' show' : '')} role="alert" aria-live="assertive">
              {s.err}
            </div>
          </>
        )}
        <Footer />
      </form>
    </LoginShell>
  );
}
